import { useState } from "react";
import CitySEOHead from "@/components/feature/CitySEOHead";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

const kentCountyCities = [
  {
    name: "Grand Rapids",
    route: "/grand-rapids",
    tagline: "Kent County's largest city — expedited passport services for all of West Michigan",
    highlights: ["Kent County hub", "Serving Wyoming, Kentwood, Walker, East Grand Rapids"],
    population: "198,000",
    badge: "Hub City",
  },
  {
    name: "Holland",
    route: "/cities/holland",
    tagline: "Ottawa County's coastal city — expedited passport processing on Lake Michigan",
    highlights: ["Lake Michigan shore", "Serving Zeeland, Hudsonville, Saugatuck"],
    population: "34,000",
    badge: null,
  },
  {
    name: "Wyoming",
    route: "/states/wyoming",
    tagline: "Southwest Kent County — expedited passport services on the 28th Street corridor",
    highlights: ["28th Street corridor", "Serving Grandville, Byron Center, Cutlerville"],
    population: "77,000",
    badge: null,
  },
  {
    name: "Kentwood",
    route: "/cities/kentwood",
    tagline: "Southeast Kent County — expedited passport services near Woodland Mall",
    highlights: ["44th Street corridor", "Serving Caledonia, East Grand Rapids, Cutlerville"],
    population: "55,000",
    badge: null,
  },
];

const faqs = [
  {
    question: "Does Kent County have a passport agency?",
    answer:
      "Kent County does not have its own regional passport agency. The nearest agency is the Detroit Passport Agency (211 W. Fort St, Suite 1300, Detroit, MI 48226), approximately 155–160 miles from Grand Rapids, Wyoming, and Kentwood. Fast Passport Center provides courier service to this agency for all Kent County residents, eliminating the need for you to travel to Detroit.",
  },
  {
    question: "How fast can I get a passport in Kent County?",
    answer:
      "With Fast Passport Center, Kent County residents can get same-day emergency service or 2–3 week expedited processing. Routine service through the post office takes 6–13 weeks plus mailing time. Our courier service handles the trip to the Detroit Passport Agency for you.",
  },
  {
    question: "Where can I get passport photos in Kent County?",
    answer:
      "Passport photos are available at CVS, Walgreens, and Rite Aid locations throughout Grand Rapids, Wyoming, Kentwood, and Holland. Many post offices also offer photo services. For best results, ensure photos meet the 2x2 inch specification with a white background.",
  },
  {
    question: "Do Kent County residents need a passport for Canada?",
    answer:
      "A valid passport is required for all air travel to Canada. For land border crossings, U.S. citizens can use a passport card, enhanced driver's license, or Trusted Traveler card. A passport book is recommended for maximum flexibility.",
  },
  {
    question: "What post offices accept passport applications in Kent County?",
    answer:
      "The Grand Rapids Main Post Office (600 Fountain St SE), Wyoming Post Office (1850 28th St SW), Kentwood Post Office (3660 29th St SE), and Holland Main Post Office (61 E 8th St) all accept passport applications. Appointments are recommended, especially during peak travel season.",
  },
  {
    question: "Can I renew my passport by mail from Kent County?",
    answer:
      "Yes, if your most recent passport is undamaged, was issued when you were 16 or older, and was issued within the last 15 years, you can renew by mail. Fast Passport Center can review your renewal application for completeness and expedite the processing for faster turnaround.",
  },
  {
    question: "What if I lost my passport in Kent County?",
    answer:
      "For lost or stolen passports, you must report it using Form DS-64 and apply in person using Form DS-11 at an acceptance facility. If you have urgent travel, Fast Passport Center can help you get a replacement quickly through our emergency service.",
  },
  {
    question: "How much does expedited passport service cost in Kent County?",
    answer:
      "Government fees are $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center offers service packages that include document review, photo verification, courier service to the Detroit Passport Agency, and tracking. Contact us for a quote based on your timeline.",
  },
];

const kentCountyServicesSchema = [
  { name: "Emergency Passport Service Kent County", description: "Same-day and next-day emergency passport processing for Kent County travelers with urgent international travel needs." },
];

export default function KentCountyPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <>
      <CitySEOHead
        city="Kent County"
        state="Michigan"
        stateAbbr="MI"
        route="/cities/kent-county"
        title="Expedited Passport Services Kent County MI | Grand Rapids, Holland, Wyoming, Kentwood | Fast Passport Center"
        description="Fast Passport Center provides emergency and expedited passport services across Kent County, Michigan. Serving Grand Rapids, Holland, Wyoming, and Kentwood. Courier service to the Detroit Passport Agency."
        geoPlacename="Kent County, Michigan"
        geoPosition="42.9634;-85.6681"
        faqs={faqs.map(f => ({ q: f.question, a: f.answer }))}
        servicesSchema={kentCountyServicesSchema}
        statePageName="Michigan"
        statePageRoute="/states/michigan"
      />
      <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-[#1A365D] overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('https://readdy.ai/api/search-image?query=abstract%20geometric%20pattern%20in%20navy%20blue%20and%20gold%20subtle%20minimal%20modern%20background%20texture%20with%20clean%20lines%20and%20soft%20shapes%20high%20quality%20professional%20design%20backdrop&width=1400&height=600&seq=kent-hero&orientation=landscape')] bg-cover bg-center" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-28">
          <div className="max-w-3xl">
            <nav className="text-sm text-white/60 mb-6">
              <a href="/" className="hover:text-white transition-colors">Home</a>
              <span className="mx-2">/</span>
              <a href="/states/michigan" className="hover:text-white transition-colors">Michigan</a>
              <span className="mx-2">/</span>
              <span className="text-white/80">Kent County</span>
            </nav>
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-4">
              County-Wide Coverage
            </p>
            <h1 className="font-serif text-3xl md:text-5xl font-bold text-white mb-6 leading-tight">
              Expedited Passport Services in Kent County, Michigan
            </h1>
            <p className="text-white/80 text-base md:text-lg leading-relaxed mb-8 max-w-2xl">
              Emergency and expedited passport processing for all of Kent County.
              Serving <strong className="text-white">Grand Rapids, Holland, Wyoming,</strong> and <strong className="text-white">Kentwood</strong> with same-day and 2–3 week service options.
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                href="tel:8772530084"
                className="inline-flex items-center gap-2 bg-[#F6C344] text-[#1A365D] font-semibold px-6 py-3 rounded-lg hover:bg-white transition-all whitespace-nowrap"
              >
                <i className="ri-phone-line"></i>
                Call 877-253-0084
              </a>
              <a
                href="/grand-rapids"
                className="inline-flex items-center gap-2 bg-white/10 text-white font-semibold px-6 py-3 rounded-lg hover:bg-white/20 transition-all whitespace-nowrap"
              >
                <i className="ri-map-pin-line"></i>
                Grand Rapids Hub
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* City Cards Grid */}
      <section className="bg-[#F7F8FA] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">
              4 Cities Served
            </p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
              Choose Your Kent County City
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
              Every city in Kent County is covered through our Grand Rapids hub.
              Select your city to view local acceptance facilities, processing times, and expedited services.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {kentCountyCities.map((city, i) => (
              <a
                key={i}
                href={city.route}
                className="group relative bg-white border border-gray-200 rounded-lg p-7 hover:border-[#F6C344] transition-all duration-200 flex flex-col"
              >
                {city.badge && (
                  <span className="absolute top-4 right-4 bg-[#1A365D] text-white text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap">
                    {city.badge}
                  </span>
                )}
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#EEF2F8] flex-shrink-0">
                    <i className="ri-map-pin-2-line text-[#1A365D] text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-[#1A365D] text-lg">{city.name}</h3>
                    <p className="text-gray-400 text-xs">Population: {city.population}</p>
                  </div>
                </div>
                <p className="text-gray-500 text-sm leading-relaxed mb-4 flex-1">{city.tagline}</p>
                <ul className="space-y-2 mb-6">
                  {city.highlights.map((h, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm text-gray-600">
                      <i className="ri-check-line text-[#F6C344] flex-shrink-0"></i>
                      {h}
                    </li>
                  ))}
                </ul>
                <div className="inline-flex items-center gap-1.5 text-[#1A365D] font-semibold text-sm group-hover:text-[#F6C344] transition-colors mt-auto whitespace-nowrap">
                  View Services{" "}
                  <i className="ri-arrow-right-line group-hover:translate-x-0.5 transition-transform"></i>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-14">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">
              Passport Services
            </p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
              How We Serve Kent County
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
              From the ArtPrize crowds of Grand Rapids to the Tulip Time tourists of Holland,
              from the 28th Street corridor of Wyoming to the Woodland Mall area of Kentwood —
              we help Kent County travelers get their passports fast.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: "ri-flight-takeoff-line",
                title: "Emergency Same-Day Service",
                desc: "For Kent County travelers with flights within 72 hours. We hand-carry your application to the Detroit Passport Agency — no road trip required.",
              },
              {
                icon: "ri-time-line",
                title: "Expedited 2–3 Week Service",
                desc: "Standard expedited processing with overnight shipping and tracking for Grand Rapids, Holland, and all Kent County residents.",
              },
              {
                icon: "ri-file-list-3-line",
                title: "New Passport Applications",
                desc: "First-time applicants in Kent County — we handle document prep, photos, and acceptance facility scheduling at your local post office.",
              },
              {
                icon: "ri-refresh-line",
                title: "Passport Renewals",
                desc: "Kent County renewals by mail or courier. We ensure your application is complete to avoid delays before your next trip.",
              },
              {
                icon: "ri-flag-line",
                title: "Gerald R. Ford Airport Travel",
                desc: "Kent County is served by GRR Airport. We help residents get proper travel documents for international flights from Grand Rapids.",
              },
              {
                icon: "ri-error-warning-line",
                title: "Lost, Stolen & Damaged",
                desc: "Fast replacement for Kent County residents who need a new passport quickly — especially for urgent business or family travel.",
              },
            ].map((svc, i) => (
              <div
                key={i}
                className="bg-[#F7F8FA] rounded-lg p-7 border border-gray-100 hover:border-[#F6C344]/40 transition-all duration-200"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#EEF2F8] mb-5">
                  <i className={`${svc.icon} text-[#1A365D] text-xl`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-base mb-2">{svc.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{svc.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Processing Options */}
      <section className="bg-[#F7F8FA] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">
              Processing Speeds
            </p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
              Passport Processing Options in Kent County
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
              Compare your options for getting a passport fast in Kent County.
              We recommend our courier service for urgent travel needs.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-lg border border-gray-200 overflow-hidden">
              <thead>
                <tr className="bg-[#1A365D]">
                  <th className="text-left text-white font-semibold text-sm px-6 py-4">Service Type</th>
                  <th className="text-left text-white font-semibold text-sm px-6 py-4">Processing Time</th>
                  <th className="text-left text-white font-semibold text-sm px-6 py-4">Best For</th>
                  <th className="text-left text-white font-semibold text-sm px-6 py-4">Cost</th>
                </tr>
              </thead>
              <tbody>
                {[
                  {
                    service: "Emergency Same-Day",
                    time: "24–72 hours",
                    best: "Flights within 3 days",
                    cost: "Gov fee + $60 expedite + service",
                    highlight: true,
                  },
                  {
                    service: "Expedited Courier",
                    time: "2–3 weeks",
                    best: "Upcoming travel in 1–2 months",
                    cost: "Gov fee + $60 expedite + service",
                    highlight: false,
                  },
                  {
                    service: "Routine Post Office",
                    time: "6–13 weeks",
                    best: "No rush, budget option",
                    cost: "Gov fee only",
                    highlight: false,
                  },
                  {
                    service: "Passport Renewal by Mail",
                    time: "6–13 weeks (routine) / 2–3 weeks (expedited)",
                    best: "Adult renewals with valid old passport",
                    cost: "$130 book / $30 card + optional $60 expedite",
                    highlight: false,
                  },
                ].map((row, i) => (
                  <tr
                    key={i}
                    className={`border-t border-gray-100 ${row.highlight ? "bg-[#F6C344]/5" : ""}`}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-[#1A365D] text-sm">{row.service}</span>
                        {row.highlight && (
                          <span className="bg-[#F6C344] text-[#1A365D] text-xs font-semibold px-2 py-0.5 rounded-full">
                            Popular
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{row.time}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{row.best}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{row.cost}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Local Area Info */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">
                Local Area
              </p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-6">
                Kent County Passport Resources
              </h2>
              <div className="space-y-4 text-gray-600 text-sm leading-relaxed">
                <p>
                  <strong className="text-[#1A365D]">Nearest Passport Agency:</strong>{" "}
                  Detroit Passport Agency — 211 W. Fort St, Suite 1300, Detroit, MI 48226.
                  Approximately 155–160 miles from Grand Rapids via I-96.
                  Appointments required for same-day service.
                </p>
                <p>
                  <strong className="text-[#1A365D]">Local Airports:</strong>{" "}
                  Gerald R. Ford International Airport (GRR) is the primary airport for Kent County,
                  located in Grand Rapids. It serves all of West Michigan with growing domestic and international routes.
                  Detroit Metro Airport (DTW) is the major international hub approximately 160 miles east.
                </p>
                <p>
                  <strong className="text-[#1A365D]">Acceptance Facilities:</strong>{" "}
                  Post offices in Grand Rapids, Wyoming, Kentwood, and Holland
                  accept passport applications. Appointments are recommended during peak season.
                </p>
                <p>
                  <strong className="text-[#1A365D]">DMV Locations:</strong>{" "}
                  Secretary of State offices in Grand Rapids, Wyoming, and Kentwood
                  can assist with REAL ID and enhanced driver's license questions.
                </p>
              </div>
              <div className="mt-6 flex flex-wrap gap-2">
                {["Grand Rapids", "Holland", "Wyoming", "Kentwood"].map(
                  (city) => (
                    <a
                      key={city}
                      href={`/${city.toLowerCase().replace(/ /g, "-")}`}
                      className="inline-flex items-center text-xs font-medium text-[#1A365D] bg-[#EEF2F8] rounded-full px-3 py-1.5 hover:bg-[#1A365D] hover:text-white transition-all whitespace-nowrap"
                    >
                      {city}
                    </a>
                  )
                )}
              </div>
            </div>
            <div className="bg-[#F7F8FA] rounded-lg p-8">
              <h3 className="font-bold text-[#1A365D] text-lg mb-4">
                Why Kent County Trusts Us
              </h3>
              <ul className="space-y-4">
                {[
                  {
                    icon: "ri-shield-check-line",
                    title: "Trusted Courier Service",
                    desc: "We hand-carry your application to the Detroit Passport Agency — no need for you to make the trip.",
                  },
                  {
                    icon: "ri-map-pin-line",
                    title: "Local Knowledge",
                    desc: "We know the Grand Rapids, Wyoming, and Kentwood post offices and their scheduling quirks.",
                  },
                  {
                    icon: "ri-customer-service-2-line",
                    title: "Personal Support",
                    desc: "Talk to a real person who understands Kent County logistics and can guide you step by step.",
                  },
                  {
                    icon: "ri-speed-line",
                    title: "Fast Turnaround",
                    desc: "Same-day emergency service and 2–3 week expedited options for all Kent County residents.",
                  },
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#EEF2F8] flex-shrink-0 mt-0.5">
                      <i className={`${item.icon} text-[#1A365D] text-sm`}></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-[#1A365D] text-sm">{item.title}</h4>
                      <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-[#F7F8FA] py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">
              Common Questions
            </p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
              Kent County Passport FAQs
            </h2>
            <p className="text-gray-500 text-base leading-relaxed">
              Answers to the most common questions about getting a passport fast in Kent County.
            </p>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div
                key={i}
                className="bg-white rounded-lg border border-gray-200 overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between gap-4 px-6 py-4 text-left cursor-pointer"
                >
                  <span className="font-semibold text-[#1A365D] text-sm">{faq.question}</span>
                  <i
                    className={`ri-arrow-down-s-line text-[#1A365D] transition-transform flex-shrink-0 ${
                      openFaq === i ? "rotate-180" : ""
                    }`}
                  ></i>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-4">
                    <p className="text-gray-500 text-sm leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[#1A365D] py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Get Your Passport Fast?
          </h2>
          <p className="text-white/70 text-base md:text-lg leading-relaxed mb-8 max-w-2xl mx-auto">
            Whether you're in Grand Rapids, Holland, Wyoming, or Kentwood —
            we make getting your passport fast and stress-free. Call now or visit your city's page.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <a
              href="tel:8772530084"
              className="inline-flex items-center gap-2 bg-[#F6C344] text-[#1A365D] font-semibold px-8 py-4 rounded-lg hover:bg-white transition-all whitespace-nowrap"
            >
              <i className="ri-phone-line"></i>
              Call 877-253-0084
            </a>
            <a
              href="/grand-rapids"
              className="inline-flex items-center gap-2 bg-white/10 text-white font-semibold px-8 py-4 rounded-lg hover:bg-white/20 transition-all whitespace-nowrap"
            >
              <i className="ri-map-pin-line"></i>
              Grand Rapids Hub
            </a>
          </div>
          <p className="text-white/40 text-sm mt-6">
            Serving all of Kent County, Michigan — 7 days a week
          </p>
        </div>
      </section>

      {/* SEO Cross Links */}
      <section className="bg-[#F7F8FA] py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <SEOCrossLinks state="MI" showCities={false} />
        </div>
      </section>
    </div>
    </>
  );
}